// Supplier performance — the ONE place raw post-event feedback is aggregated
// into the derived figures every surface reads (Overview Performance card, the
// compare screen's Track record, the recognition ladders, reporting). Per the
// brief §13: no component re-aggregates raw feedback for itself.
//
// Rules (brief §6):
//   - Window: last 5 completed jobs OR 24 months, whichever reaches FURTHER
//     (a job counts if it's within 24 months OR among the 5 most recent).
//   - Below 3 completed jobs → raw responses, not a derived rating.
//   - Every figure carries its basis (the count behind it).
//   - A supplier with none reads "not yet rated", never zero.
//   - Open incidents are NOT subject to the window — they stay until closed.

const MS_MONTH = 30.4375 * 24 * 60 * 60 * 1000;
const WINDOW_MONTHS = 24;
const MIN_JOBS = 3;
const RECENT_N = 5;

const isCompleted = (f) => f && (f.completed_at || f.status === 'complete');

// A single job's outcome bucket, for "last job" + the raw-response fallback.
export const jobOutcome = (f) => {
  if (!f) return null;
  if (f.safety === 'reportable') return 'reportable';
  const negative = f.delivered === 'no' || f.on_time === 'no_show' || f.use_again === 'no';
  if (negative) return 'issues';
  const minor = f.delivered === 'mostly' || f.on_time === 'late' || f.safety === 'minor' || f.use_again === 'reservations';
  return minor ? 'mostly' : 'well';
};

export function deriveSupplierPerformance(feedbackRows = [], incidents = [], now = new Date()) {
  const completed = (feedbackRows || [])
    .filter(isCompleted)
    .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
  const totalCompleted = completed.length;

  const cutoff = now.getTime() - WINDOW_MONTHS * MS_MONTH;
  const windowed = completed.filter((f, i) => i < RECENT_N || new Date(f.completed_at || 0).getTime() >= cutoff);

  const open = (incidents || []).filter(i => i && !i.closed_at);
  const openIncidents = {
    total: open.length,
    minor: open.filter(i => i.severity === 'minor').length,
    reportable: open.filter(i => i.severity === 'reportable').length,
  };

  const lastJob = completed[0]
    ? { at: completed[0].completed_at, outcome: jobOutcome(completed[0]) }
    : null;

  // Not enough to derive an average — surface the raw responses instead.
  if (windowed.length < MIN_JOBS) {
    return {
      rated: false,
      totalCompleted,
      windowCount: windowed.length,
      raw: windowed.map(f => ({ at: f.completed_at, outcome: jobOutcome(f), delivered: f.delivered, on_time: f.on_time, use_again: f.use_again, safety: f.safety })),
      quality: null,
      useAgain: null,
      onTime: null,
      openIncidents,
      hasRecentNegative: open.length > 0,
      lastJob,
    };
  }

  // Quality ← "delivered what was agreed" (yes=1, mostly=.5, no=0).
  const delivered = windowed.filter(f => f.delivered);
  const qualityAvg = delivered.length
    ? delivered.reduce((s, f) => s + (f.delivered === 'yes' ? 1 : f.delivered === 'mostly' ? 0.5 : 0), 0) / delivered.length
    : null;

  // General ← "would you use them again".
  const useAgain = windowed.filter(f => f.use_again);
  const useAgainYes = useAgain.filter(f => f.use_again === 'yes').length;

  const onTime = windowed.filter(f => f.on_time);
  const onTimeYes = onTime.filter(f => f.on_time === 'yes').length;

  // Watchlist signal: a "would not use again" in the recent set, or any open
  // incident.
  const hasRecentNegative = windowed.slice(0, RECENT_N).some(f => f.use_again === 'no') || open.length > 0;

  return {
    rated: true,
    totalCompleted,
    windowCount: windowed.length,
    quality: qualityAvg == null ? null : { score: qualityAvg, stars: Math.max(1, Math.round(qualityAvg * 5)), of: delivered.length },
    useAgain: useAgain.length ? { yes: useAgainYes, of: useAgain.length, pct: Math.round((useAgainYes / useAgain.length) * 100) } : null,
    onTime: onTime.length ? { yes: onTimeYes, of: onTime.length } : null,
    openIncidents,
    hasRecentNegative,
    lastJob,
  };
}

// "from N completed jobs" basis string, or the not-yet-rated line.
export const performanceBasis = (perf) =>
  perf?.totalCompleted ? `from ${perf.totalCompleted} completed job${perf.totalCompleted === 1 ? '' : 's'}` : 'not yet rated';

// The raw answers, summarised for display below the 3-job threshold. The page
// used to promise "raw responses shown until 3 jobs" and then print an em-dash
// in every column — the answers were derived and thrown away. These turn one or
// two jobs into something readable without pretending it's an average.
const ANSWER_WORDS = {
  delivered: { yes: 'Yes', mostly: 'Mostly', no: 'No' },
  on_time:   { yes: 'Yes', late: 'Late', no_show: 'No show' },
  use_again: { yes: 'Yes', reservations: 'With reservations', no: 'No' },
};

export function rawAnswerSummary(perf, field) {
  const rows = (perf?.raw || []).filter(r => r && r[field]);
  if (rows.length === 0) return null;
  // One job: just say what they said. Several: count each distinct answer, so
  // "Yes · 2, Late · 1" reads as the record it is rather than a false average.
  const words = ANSWER_WORDS[field] || {};
  if (rows.length === 1) return words[rows[0][field]] || rows[0][field];
  const counts = rows.reduce((acc, r) => {
    const w = words[r[field]] || r[field];
    acc[w] = (acc[w] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).map(([w, n]) => `${w} · ${n}`).join(', ');
}

// How each figure is worked out, in words — shown behind the info icons so the
// numbers can be questioned rather than just believed.
export const PERFORMANCE_EXPLAINERS = {
  window: `The last ${RECENT_N} completed jobs, or anything in the past ${WINDOW_MONTHS} months — whichever reaches further back. Averages only start once there are ${MIN_JOBS} completed jobs; below that the individual answers are shown instead.`,
  quality: 'From "did they deliver what was agreed" on each job: Yes counts full, Mostly counts half, No counts nothing. Averaged across the jobs in the window and shown out of 5.',
  useAgain: 'The share of jobs in the window where the answer to "would you use them again" was Yes. "With reservations" is not counted as a Yes.',
  onTime: 'How many jobs in the window were answered Yes to "did they turn up when they said they would" — counted, not averaged, so a single late job stays visible.',
  lastJob: 'The most recent completed job and how it went overall. A reportable safety incident outranks everything else; otherwise any "no" reads as issues, and a "mostly" or "late" reads as mostly.',
};
